// ===================== Player =====================
import { CONFIG } from '../config.js';
import { getWeapon } from './registries.js';

export class Player {
  constructor(charDef, metaBonuses = {}) {
    const b = CONFIG.player;
    this.char = charDef;
    this.spriteKey = charDef.sprite || 'player_wanderer';

    // Core transform.
    this.x = 0; this.y = 0;
    this.vx = 0; this.vy = 0;
    this.r = 15;
    this.facing = 1;     // 1 right, -1 left
    this.aimAngle = 0;   // toward nearest enemy / cursor
    this.bob = 0;
    this.lean = 0;       // procedural lean into movement direction (render juice)

    // Stats (character overrides > config defaults, then meta bonuses).
    const s = charDef.stats || {};
    this.maxHp = (s.maxHp ?? b.maxHp) + (metaBonuses.maxHp || 0);
    this.hp = this.maxHp;
    this.moveSpeed = (s.moveSpeed ?? b.moveSpeed) * (1 + (metaBonuses.moveSpeed || 0));
    this.damageMult = (s.damageMult ?? b.damageMult) * (1 + (metaBonuses.damage || 0));
    this.cooldownMult = 1 / (s.attackSpeedMult ?? b.attackSpeedMult); // lower = faster
    this.areaMult = s.areaMult ?? b.areaMult;
    this.projSpeedMult = s.projectileSpeedMult ?? b.projectileSpeedMult;
    this.projMult = s.projMult ?? b.projMult;                 // MULTIPLICATION (×)
    this.projPickups = 0;                                     // golden ✖ pickups collected
    this.projAdd = (s.projAdd ?? 0) + (metaBonuses.projAdd || 0); // flat +N projectiles (Duplicate / Wild Hunt)
    this.durationMult = s.durationMult ?? 1;                  // effect/zone duration
    this.curse = s.curse ?? 0;                                // +enemy count/speed/hp, +rewards
    this.critChance = (s.critChance ?? b.critChance) + (metaBonuses.crit || 0);
    this.critMult = (s.critMult ?? b.critMult) + (metaBonuses.critMult || 0);
    this.armor = (s.armor ?? b.armor) + (metaBonuses.armor || 0);
    this.regen = (s.regen ?? b.regen) + (metaBonuses.regen || 0);
    this.luck = (s.luck ?? b.luck) + (metaBonuses.luck || 0);
    this.xpMult = (s.xpMult ?? b.xpMult) * (1 + (metaBonuses.xp || 0));
    this.goldMult = (s.goldMult ?? b.goldMult) * (1 + (metaBonuses.gold || 0));
    this.magnetMult = s.magnetMult ?? b.magnetMult;
    this.pickupRadius = (s.pickupRadius ?? b.pickupRadius) * (1 + (metaBonuses.magnet || 0));
    this.invulnAfterHit = s.invulnAfterHit ?? b.invulnAfterHit;
    this.reviveCount = (s.reviveCount ?? b.reviveCount) + (metaBonuses.revive || 0);

    // Progression.
    this.level = 1;
    this.xp = 0;
    this.xpToNext = this._xpForLevel(1);

    // Element affinity — the "main guy" starts Neutral.
    this.element = charDef.startElement || 'neutral';
    this.elementLevel = 1;

    // Weapons & passives.
    this.weapons = [];              // [{ def, level, timer }]
    this.passives = new Map();      // id -> CARDS TAKEN (drives the offer pool + caps)

    // Combat state.
    this.iframes = 0;
    this.dashCd = 0;

    if (charDef.startWeapon) this.addWeapon(charDef.startWeapon);
  }

  _xpForLevel(level) {
    const c = CONFIG.xp;
    return Math.round(c.base * Math.pow(c.growth, level - 1) + c.flat * level);
  }

  addWeapon(id) {
    if (this.hasWeapon(id)) { this.upgradeWeapon(id); return; }
    const def = getWeapon(id);
    if (!def) return;
    this.weapons.push({ def, level: 1, timer: 0.35 });
  }
  hasWeapon(id) { return this.weapons.some((w) => w.def.id === id); }
  weaponInst(id) { return this.weapons.find((w) => w.def.id === id); }
  upgradeWeapon(id) {
    const w = this.weaponInst(id);
    if (w && w.level < w.def.maxLevel) w.level++;
  }
  removeWeapon(id) {
    const i = this.weapons.findIndex((w) => w.def.id === id);
    if (i >= 0) this.weapons.splice(i, 1);
  }
  // Evolve a base weapon into its evolved form (keeps the slot).
  evolveWeapon(baseId, evolvedId, getDef) {
    this.removeWeapon(baseId);
    const def = getDef(evolvedId);
    if (def && !this.hasWeapon(evolvedId)) this.weapons.push({ def, level: 1, timer: 0.2 });
  }
  weaponMaxed(id) {
    const w = this.weaponInst(id);
    return w && w.level >= w.def.maxLevel;
  }
  weaponCount() { return this.weapons.length; }

  // `passives` counts CARDS TAKEN — never anything else. The offer pool, the
  // per-passive max and the 6-slot cap all read it, so anything that bumps it
  // spends one of the player's future cards.
  passiveLevel(id) { return this.passives.get(id) || 0; }
  bumpPassive(id) { this.passives.set(id, this.passiveLevel(id) + 1); }

  // The golden ✖ stacks MULTIPLICATION without touching the passive map:
  // collecting a power-up must never delete a card from your future offers.
  // Its bonus tapers (CONFIG.multiply) — the Nth pickup carries decay^(N-1) of
  // the first one's — so the pickup channel stops growing exponentially with
  // run length. Always > 1: picking one up is never worse than leaving it.
  multiplyFactor() {
    const m = CONFIG.multiply;
    return 1 + m.pickupBonus * Math.pow(m.pickupDecay, this.projPickups);
  }
  gainMultiply() {
    const factor = this.multiplyFactor();
    this.projMult *= factor;
    this.projPickups++;
    return factor;
  }

  // Passive slots for the HUD, in the order they were acquired. Multiplication
  // reports the combined effective multiplier instead of a level, because it
  // arrives from two channels (cards and pickups) and a level number would
  // only ever describe one of them — and it shows up on a pickup alone, with
  // no card taken and no passive slot spent.
  passiveSlots() {
    const out = [];
    for (const [id, level] of this.passives) {
      out.push({ id, level, label: id === 'multiply' ? this.multiplyLabel() : String(level) });
    }
    if (this.projPickups > 0 && !this.passives.has('multiply')) {
      out.push({ id: 'multiply', level: 0, label: this.multiplyLabel() });
    }
    return out;
  }
  multiplyLabel() { return '×' + this.projMult.toFixed(2); }

  gainXp(amount) {
    this.xp += amount * this.xpMult;
    let leveled = 0;
    while (this.xp >= this.xpToNext) {
      this.xp -= this.xpToNext;
      this.level++;
      leveled++;
      this.xpToNext = this._xpForLevel(this.level);
    }
    return leveled;
  }

  heal(amount) { this.hp = Math.min(this.maxHp, this.hp + amount); }

  // Returns actual damage taken after armor (min 1 unless armor huge).
  takeDamage(raw) {
    const reduced = Math.max(raw - this.armor, raw * 0.15);
    this.hp -= reduced;
    this.iframes = this.invulnAfterHit;
    return reduced;
  }
}
